

import React from 'react';
import { Calendar, Users, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatDate } from "@/lib/data";

interface Event {
  id: number;
  event_name?: string;
  name?: string;
  event_date?: string;
  date?: string;
  guest_count?: number;
  guests?: number;
  status?: string;
}

interface EventsWidgetProps {
  events: Event[];
  loading?: boolean;
}

const statusColors = {
  confirmed: 'bg-green-100 text-green-800',
  pending: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-blue-100 text-blue-800',
  cancelled: 'bg-red-100 text-red-800',
};

export function EventsWidget({ events, loading = false }: EventsWidgetProps) {
  const displayEvents = events.slice(0, 5);

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-stone-800">Upcoming Events</h3>
        <Link to="/events" className="text-xs text-amber-600 hover:text-amber-700 font-medium">
          View all
        </Link>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-20 bg-gray-100 rounded animate-pulse" />
          ))}
        </div>
      ) : displayEvents.length === 0 ? (
        <p className="text-sm text-stone-500 text-center py-8">No upcoming events</p>
      ) : (
        <div className="space-y-3">
          {displayEvents.map((event) => (
            <Link
              key={event.id}
              to={`/events/${event.id}`}
              className="block p-3 rounded-lg border border-stone-100 hover:border-stone-300 hover:bg-stone-50 transition-all group"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate group-hover:text-blue-600">
                    {event.event_name || event.name}
                  </p>

                  <div className="flex flex-wrap gap-2 mt-2 text-xs text-stone-500">
                    {(event.event_date || event.date) && (
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        <span>{formatDate(event.event_date || event.date!)}</span>
                      </div>
                    )}

                    {(event.guest_count || event.guests) && (
                      <div className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        <span>{event.guest_count || event.guests} guests</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {event.status && (
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      statusColors[event.status as keyof typeof statusColors] || 'bg-gray-100 text-gray-800'
                    }`}>
                      {event.status}
                    </span>
                  )}
                  <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
